import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { syncTokenToSW } from "@/lib/periodicSync";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { isNativeApp, isNativeIOS, isNativeAndroid } from "@/lib/platform";

/**
 * SocialAuthButtons — "Continue with Google" + "Continue with Apple".
 *
 * Google → SELF-HOSTED Google Identity Services (@react-oauth/google).
 *  - Uses OUR OWN Google Cloud OAuth client (see REACT_APP_GOOGLE_CLIENT_ID).
 *  - No Emergent branding anywhere — consent screen shows PrepMasters and the
 *    account picker reads "to continue to prepmaster.ph".
 *  - Auth-code flow: Google returns a one-time code → we exchange it for an
 *    access_token client-side → fetch userinfo → POST id_token to our backend
 *    which verifies it against Google's JWKS.
 *
 * Apple → official Sign in with Apple (web) JS SDK using OUR Services ID.
 *
 * Props:
 *  - externalAcceptedTerms: when true, hide the internal terms checkbox
 *    (parent Register form already has one). Any Google/Apple request
 *    from this component then sends `accepted_terms: true` unconditionally.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const APPLE_SDK_URL =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || "";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

export default function SocialAuthButtons({ externalAcceptedTerms = false, hideInternalCheckbox = false } = {}) {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [appleReady, setAppleReady] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  // Only shown when the parent doesn't already gate submission with its own
  // terms checkbox (e.g. Login page). Register page passes
  // hideInternalCheckbox=true and forwards its own state via
  // externalAcceptedTerms so we don't duplicate the checkbox on-screen.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const effectiveAccepted = hideInternalCheckbox ? externalAcceptedTerms : acceptedTerms;

  // Lazy-load Apple's JS SDK (once per browser session).
  useEffect(() => {
    if (!APPLE_CLIENT_ID) return undefined;
    if (window.AppleID && window.AppleID.auth) {
      setAppleReady(true);
      return undefined;
    }
    let script = document.querySelector(`script[src="${APPLE_SDK_URL}"]`);
    const onReady = () => {
      try {
        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: "name email",
          redirectURI: window.location.origin + "/auth/apple/callback",
          state: "prepmasters",
          usePopup: true,
        });
        setAppleReady(true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("AppleID.auth.init failed:", e);
      }
    };
    if (!script) {
      script = document.createElement("script");
      script.src = APPLE_SDK_URL;
      script.async = true;
      script.defer = true;
      script.onload = onReady;
      document.body.appendChild(script);
    } else {
      onReady();
    }
  }, []);

  const applyLoginResponse = async (r) => {
    const { token, user, is_new_user, replaced_previous_device, profile_completion } = r.data;
    localStorage.setItem("pm_token", token);
    localStorage.setItem("pm_user", JSON.stringify(user));
    try { await syncTokenToSW(token); } catch (e) { /* noop */ }
    setUser(user);
    if (replaced_previous_device) {
      toast.success("Signed in. Your previous device was logged out.", { duration: 5000 });
    } else if (is_new_user) {
      toast.success("Welcome to PrepMaster! Pick a course to start reviewing.", { duration: 5000 });
    } else {
      toast.success(`Welcome, ${user.full_name || user.email}!`);
    }
    const target = (is_new_user || (profile_completion && !profile_completion.is_complete))
      ? "/student/enrollments"
      : (user.role === "student" ? "/student" : "/admin");
    nav(target, { replace: true });
  };

  // Google Identity Services — auth-code flow. On success Google gives us a
  // one-time authorization code; we exchange it for an ID token via Google's
  // token endpoint (client-side, no client_secret needed since we're a public
  // client), then post the ID token to /auth/google/verify for server-side
  // signature + audience validation.
  const runGoogleLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResponse) => {
      setGoogleBusy(true);
      try {
        // Implicit flow returns an access_token. We hand it to the backend,
        // which calls Google's userinfo endpoint to fetch verified claims.
        // The access_token was only issued because the user just approved our
        // consent screen, so it's a trusted, authenticated call.
        const r = await axios.post(`${API}/auth/google/verify`, {
          credential: tokenResponse.access_token,
          access_token_flow: true,
          device_id: undefined,
          device_name: navigator.userAgent.slice(0, 160),
          accepted_terms: effectiveAccepted,
        });
        await applyLoginResponse(r);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Google sign-in failed";
        toast.error(typeof msg === "string" ? msg : "Google sign-in failed");
      } finally {
        setGoogleBusy(false);
      }
    },
    onError: () => {
      toast.error("Google sign-in was cancelled or failed. Please try again.");
      setGoogleBusy(false);
    },
  });

  const handleGoogle = async () => {
    // Native (iOS/Android): use Capacitor Google Auth plugin (real native
    // GoogleSignIn / Play Services flow — the WKWebView popup approach the
    // web SDK relies on doesn't work inside a native shell).
    if (isNativeApp()) {
      setGoogleBusy(true);
      try {
        const { GoogleAuth } = await import("@codetrix-studio/capacitor-google-auth");
        // IMPORTANT: on native we let the plugin read iosClientId /
        // serverClientId from capacitor.config.ts. Passing the WEB client ID
        // here (as we used to) crashes the native SDK because Google's iOS
        // SDK strictly requires an iOS-type OAuth client.
        await GoogleAuth.initialize();
        const result = await GoogleAuth.signIn();
        const idToken = result?.authentication?.idToken;
        if (!idToken) throw new Error("Google did not return an ID token");
        const r = await axios.post(`${API}/auth/google/verify`, {
          credential: idToken,
          device_id: undefined,
          device_name: `${navigator.userAgent.slice(0, 120)} (native)`,
          accepted_terms: effectiveAccepted,
        });
        await applyLoginResponse(r);
      } catch (err) {
        const code = err?.code || err?.error || "";
        if (String(code).includes("12501") || String(err?.message || "").toLowerCase().includes("cancel")) {
          // User closed the Google sheet — silent.
        } else {
          const msg = err?.response?.data?.detail || err?.message || "Google sign-in failed";
          toast.error(typeof msg === "string" ? msg : "Google sign-in failed");
        }
      } finally {
        setGoogleBusy(false);
      }
      return;
    }
    // Web: existing @react-oauth/google flow.
    if (!GOOGLE_CLIENT_ID) {
      toast.error("Google Sign-In is not configured yet. Please contact support.");
      return;
    }
    setGoogleBusy(true);
    runGoogleLogin();
  };

  const handleApple = async () => {
    // Native iOS: use the real Apple Sign-In via
    // @capacitor-community/apple-sign-in (calls into AuthenticationServices).
    if (isNativeIOS()) {
      setAppleBusy(true);
      // Track which phase failed so the toast tells us Apple SDK vs HTTP.
      let phase = "apple-sdk";
      try {
        const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
        const res = await SignInWithApple.authorize({
          // Native iOS uses the App ID as the audience — not the web Services ID.
          clientId: "ph.prepmaster.ios",
          redirectURI: undefined,      // Native flow doesn't use redirect
          scopes: "email name",
          state: "prepmasters-native",
          nonce: Math.random().toString(36).slice(2),
        });
        const idToken = res?.response?.identityToken;
        if (!idToken) throw new Error("Apple did not return an ID token");
        phase = "http-backend";
        // Log the URL we're about to POST to so it shows in Safari WebInspector.
        // eslint-disable-next-line no-console
        console.log("[apple-login] POST", `${API}/auth/apple/session`);
        const r = await axios.post(`${API}/auth/apple/session`, {
          id_token: idToken,
          user: (res?.response?.givenName || res?.response?.familyName || res?.response?.email)
            ? {
                name: { firstName: res.response.givenName, lastName: res.response.familyName },
                email: res.response.email,
              }
            : undefined,
          device_id: undefined,
          device_name: `${navigator.userAgent.slice(0, 120)} (native ios)`,
          accepted_terms: effectiveAccepted,
        }, { timeout: 30000 });
        await applyLoginResponse(r);
      } catch (err) {
        const code = err?.code || err?.error || "";
        if (String(code).includes("1001") || String(err?.message || "").toLowerCase().includes("cancel")) {
          // User cancelled — silent.
        } else {
          // Build a diagnostic message: which phase failed + status/code.
          const rawMsg = err?.response?.data?.detail || err?.message || "unknown";
          const status = err?.response?.status;
          const label =
            phase === "apple-sdk"
              ? `Apple SDK failed: ${rawMsg}${code ? ` (${code})` : ""}`
              : status
                ? `Backend ${status}: ${rawMsg}`
                : `Cannot reach ${API}: ${rawMsg}`;
          // eslint-disable-next-line no-console
          console.error("[apple-login] phase=", phase, "err=", err, "response=", err?.response);
          toast.error(label, { duration: 8000 });
        }
      } finally {
        setAppleBusy(false);
      }
      return;
    }
    // Web: existing AppleID.auth.signIn() popup flow.
    if (!appleReady) {
      toast.error("Apple Sign-In is still loading. Please try again in a moment.");
      return;
    }
    setAppleBusy(true);
    try {
      const data = await window.AppleID.auth.signIn();
      const idToken = data?.authorization?.id_token;
      if (!idToken) {
        toast.error("Apple did not return a sign-in token. Please try again.");
        setAppleBusy(false);
        return;
      }
      const r = await axios.post(`${API}/auth/apple/session`, {
        id_token: idToken,
        user: data?.user || undefined,
        device_id: undefined,
        device_name: navigator.userAgent.slice(0, 160),
        accepted_terms: effectiveAccepted,
      });
      await applyLoginResponse(r);
    } catch (err) {
      const code = err?.error || err?.code || "";
      if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") {
        // user closed the popup — silent
      } else {
        const msg = err?.response?.data?.detail || err?.message || "Apple sign-in failed";
        toast.error(typeof msg === "string" ? msg : "Apple sign-in failed");
      }
    } finally {
      setAppleBusy(false);
    }
  };

  return (
    <div className="space-y-2.5" data-testid="social-auth-buttons">
      {/* Inline Terms & Privacy acceptance for social sign-in. Hidden when
          the parent form (Register) already gates submission with its own
          checkbox — externalAcceptedTerms is then forwarded as `true` so
          new social users don't lose the acceptance on POST. */}
      {!hideInternalCheckbox && (
        <label className="flex items-start gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-3 py-2 cursor-pointer" data-testid="social-accept-terms-label">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
            data-testid="social-accept-terms-checkbox"
          />
          <span className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed">
            I have read and agree to the{" "}
            <Link to="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-700 font-semibold hover:underline">Privacy Policy</Link>
            {" "}and{" "}
            <Link to="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-blue-700 font-semibold hover:underline">Terms of Use</Link>.
          </span>
        </label>
      )}

      <Button
        type="button"
        variant="outline"
        className="w-full h-11 flex items-center justify-center gap-3 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-60"
        onClick={handleGoogle}
        disabled={(!isNativeApp() && !GOOGLE_CLIENT_ID) || googleBusy || !effectiveAccepted}
        data-testid="continue-with-google-btn"
      >
        {googleBusy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <GoogleGlyph className="w-5 h-5" />
        )}
        <span className="font-medium">Continue with Google</span>
      </Button>

      {/* Apple Sign-In: hidden on native Android because Apple's web redirect
          flow rejects Capacitor's `https://localhost` origin ("Invalid web
          redirect url"). iOS uses the native plugin; web uses AppleID.auth JS. */}
      {!isNativeAndroid() && (
        <Button
          type="button"
          variant="outline"
          className="w-full h-11 flex items-center justify-center gap-3 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-60"
          onClick={handleApple}
          disabled={(!isNativeIOS() && !APPLE_CLIENT_ID) || appleBusy || !effectiveAccepted}
          data-testid="continue-with-apple-btn"
        >
          {appleBusy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <AppleGlyph className="w-5 h-5" />
          )}
          <span className="font-medium">Continue with Apple</span>
        </Button>
      )}
    </div>
  );
}

function GoogleGlyph(props) {
  return (
    <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8a12 12 0 1 1 7.9-21l5.7-5.7A20 20 0 1 0 44 24c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7A20 20 0 0 0 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.2A12 12 0 0 1 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5A20 20 0 0 0 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4 5.6l6.3 5.2C41.4 36 44 30.5 44 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}

function AppleGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M16.365 12.345c-.02-2.072 1.692-3.067 1.77-3.116-.965-1.41-2.469-1.603-3.005-1.624-1.278-.13-2.495.755-3.144.755-.65 0-1.654-.736-2.722-.716-1.399.02-2.692.815-3.412 2.07-1.456 2.521-.371 6.253 1.046 8.302.691 1.003 1.515 2.13 2.598 2.089 1.043-.04 1.437-.674 2.696-.674 1.258 0 1.612.674 2.714.654 1.121-.02 1.83-1.022 2.516-2.029.794-1.164 1.122-2.293 1.143-2.351-.025-.011-2.193-.84-2.214-3.36zM14.32 5.318c.578-.7.967-1.673.86-2.642-.831.034-1.838.553-2.434 1.252-.535.62-1.003 1.611-.876 2.561.928.072 1.872-.471 2.45-1.171z"
        fill="currentColor"
      />
    </svg>
  );
}
